import { useState, useEffect } from 'react'
import { customersAPI, subscriptionsAPI, invoicesAPI } from '../../services/api'
import { logger } from '@/utils/logger'

const statusLabels = {
  NEW: 'Neu',
  DUE: 'Fällig',
  OVERDUE: 'Überfällig',
  REMINDED: 'Angemahnt',
  PAID: 'Bezahlt',
}

const statusTagClass = {
  NEW: 'wc-tag-outline',
  DUE: 'wc-tag-outline',
  OVERDUE: 'wc-tag-neutral',
  REMINDED: 'wc-tag-outline',
  PAID: 'wc-tag-accent',
}

export default function InvoiceManagement() {
  const [invoices, setInvoices] = useState([])
  const [customers, setCustomers] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState<any>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [formData, setFormData] = useState({
    invoice_number: '',
    customer_id: '',
    subscription_id: '',
    amount: '',
    description: '',
    status: 'NEW',
    issue_date: '',
    due_date: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [invoicesResponse, customersResponse, subscriptionsResponse] = await Promise.all([
        invoicesAPI.getAllAdmin(),
        customersAPI.getAll(),
        subscriptionsAPI.getAllAdmin()
      ])
      setInvoices(invoicesResponse.data)
      setCustomers(customersResponse.data)
      setSubscriptions(subscriptionsResponse.data)
    } catch (err: any) {
      setError('Fehler beim Laden der Daten')
      logger.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const getCustomerName = (customerId: number) => {
    const customer = customers.find((c: any) => c.id === customerId)
    return customer ? (customer.company_name || customer.username) : 'Unbekannt'
  }

  const getCustomerSubscriptions = (customerId: number) => {
    return subscriptions.filter((s: any) => s.customer_id === parseInt(customerId as any))
  }

  const handleOpenModal = (invoice: any = null) => {
    if (invoice) {
      setEditingInvoice(invoice)
      setFormData({
        invoice_number: invoice.invoice_number,
        customer_id: invoice.customer_id,
        subscription_id: invoice.subscription_id || '',
        amount: invoice.amount,
        description: invoice.description || '',
        status: invoice.status,
        issue_date: invoice.issue_date ? new Date(invoice.issue_date).toISOString().split('T')[0] : '',
        due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : ''
      })
    } else {
      setEditingInvoice(null)
      setFormData({
        invoice_number: '',
        customer_id: '',
        subscription_id: '',
        amount: '',
        description: '',
        status: 'NEW',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: ''
      })
    }
    setSelectedFile(null)
    setShowModal(true)
    setError('')
    setSuccess('')
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingInvoice(null)
    setSelectedFile(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingInvoice) {
        const submitData = {
          ...formData,
          customer_id: parseInt(formData.customer_id as any),
          subscription_id: formData.subscription_id ? parseInt(formData.subscription_id as any) : null,
          amount: parseFloat(formData.amount)
        }
        await invoicesAPI.update(editingInvoice.id, submitData)

        if (selectedFile) {
          await invoicesAPI.uploadFile(editingInvoice.id, selectedFile)
        }

        setSuccess('Rechnung erfolgreich aktualisiert')
      } else {
        const apiFormData = new FormData()
        apiFormData.append('invoice_number', formData.invoice_number)
        apiFormData.append('customer_id', formData.customer_id)
        if (formData.subscription_id) {
          apiFormData.append('subscription_id', formData.subscription_id)
        }
        apiFormData.append('amount', formData.amount)
        if (formData.description) {
          apiFormData.append('description', formData.description)
        }
        apiFormData.append('status', formData.status)
        apiFormData.append('issue_date', new Date(formData.issue_date).toISOString())
        apiFormData.append('due_date', new Date(formData.due_date).toISOString())
        if (selectedFile) {
          apiFormData.append('file', selectedFile)
        }

        await invoicesAPI.create(apiFormData)
        setSuccess('Rechnung erfolgreich erstellt')
      }

      await loadData()
      handleCloseModal()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern der Rechnung')
      logger.error('Error saving invoice:', err)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Möchten Sie diese Rechnung wirklich löschen?')) return

    try {
      await invoicesAPI.delete(id)
      setSuccess('Rechnung erfolgreich gelöscht')
      await loadData()
    } catch (err: any) {
      setError('Fehler beim Löschen der Rechnung')
      logger.error('Error deleting invoice:', err)
    }
  }

  const handleDownload = async (invoice: any) => {
    try {
      const response = await invoicesAPI.download(invoice.id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', invoice.file_name || `invoice_${invoice.invoice_number}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err: any) {
      setError('Fehler beim Herunterladen der Rechnung')
      logger.error('Error downloading invoice:', err)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Rechnungsverwaltung</h1>
        <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
          + Neue Rechnung
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div className="wc-card wc-elev-sm" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="wc-table">
            <thead>
              <tr>
                <th>Rechnungsnr.</th>
                <th>Kunde</th>
                <th>Betrag</th>
                <th>Rechnungsdatum</th>
                <th>Fälligkeitsdatum</th>
                <th>Status</th>
                <th>Datei</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '48px 0' }} className="wc-muted">
                    Keine Rechnungen gefunden
                  </td>
                </tr>
              ) : (
                invoices.map((invoice: any) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number}</td>
                    <td>
                      <div>{getCustomerName(invoice.customer_id)}</div>
                      {invoice.customer_email && (
                        <div className="wc-muted" style={{ fontSize: 12 }}>{invoice.customer_email}</div>
                      )}
                    </td>
                    <td>€{invoice.amount.toFixed(2)}</td>
                    <td>{new Date(invoice.issue_date).toLocaleDateString('de-DE')}</td>
                    <td>{new Date(invoice.due_date).toLocaleDateString('de-DE')}</td>
                    <td>
                      <span className={`wc-tag ${statusTagClass[invoice.status as keyof typeof statusTagClass]}`}>
                        {statusLabels[invoice.status as keyof typeof statusLabels]}
                      </span>
                    </td>
                    <td>
                      {invoice.file_path ? (
                        <button onClick={() => handleDownload(invoice)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                          Download
                        </button>
                      ) : (
                        <span className="wc-muted">Keine Datei</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleOpenModal(invoice)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="wc-btn wc-btn-ghost"
                        style={{ padding: '4px 8px', color: '#fca5a5' }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="wc-modal-overlay" onClick={handleCloseModal}>
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>
              {editingInvoice ? 'Rechnung bearbeiten' : 'Neue Rechnung'}
            </h3>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Rechnungsnummer *</label>
                  <input
                    type="text"
                    required
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Kunde *</label>
                  <select
                    required
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value, subscription_id: '' })}
                    className="wc-input"
                  >
                    <option value="">Bitte wählen</option>
                    {customers.map((customer: any) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name || customer.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wc-field">
                  <label>Abonnement (optional)</label>
                  <select
                    value={formData.subscription_id}
                    onChange={(e) => setFormData({ ...formData, subscription_id: e.target.value })}
                    className="wc-input"
                    disabled={!formData.customer_id}
                  >
                    <option value="">Kein Abo</option>
                    {formData.customer_id && getCustomerSubscriptions(parseInt(formData.customer_id)).map((sub: any) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.app?.name || `Abo #${sub.id}`} - {sub.plan}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wc-field">
                  <label>Betrag (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Rechnungsdatum *</label>
                  <input
                    type="date"
                    required
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Fälligkeitsdatum *</label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="wc-input"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>PDF-Datei hochladen</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="wc-input"
                  />
                  {editingInvoice?.file_path && !selectedFile && (
                    <p className="wc-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
                      Aktuelle Datei: {editingInvoice.file_name}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 4 }}>
                <button type="button" onClick={handleCloseModal} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="wc-btn wc-btn-primary">
                  {editingInvoice ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
